import { TooltipWebV3 } from '@components/TaskList/TooltipWebV3';
import { useNavigation } from '@react-navigation/native';
import { Field, useFormikContext } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, ScrollView } from 'react-native';

import { Alert } from '@components/Alert';
import Avatar from '@components/Avatar/Avatar';
import { EmailAvatar } from '@components/Avatar/EmailAvatar';
import { CancelConfirmTaskAndProjectModal } from '@components/Modals/CancelConfirmTaskAndProjectModal';
import { ConfirmModal } from '@components/Modals/ConfirmModal';
import { WebModal } from '@components/Modals/WebModal.web';
import { WebModalV3 } from '@components/Modals/WebModalV3.web';
import { Box, Text } from '@components/Restyle';
import { Badge } from '@components/shared/Badges/Badge';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import TextField from '@components/shared/TextField/TextField';
import { getPriority } from '@components/TaskCard/TaskCard';
import { BaseButtonV3 } from '@components/TaskList/BaseButtonV3';
import { ProjectSelectV3 } from '@components/TaskList/ProjectSelectV3';
import { SimpleHoverable } from '@components/TaskList/SimpleHoverable';
import {
  TaskInviteOrAssignee,
  UserSearchType,
} from '@components/TaskList/TaskInviteOrAssignee.web';
import { TaskRoleSelectorWeb } from '@components/TaskList/TaskRoleSelector.web';
import { FormValues } from '@components/Tasks/TaskFormStack';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import {
  ProjectSortOption,
  TaskMemberRole,
  useListTasksNamesUnderProjectLazyQuery,
  User,
  Project,
  useGetProjectLazyQuery,
} from '@graphql/generated';
import { useListProjectsBasicInfoFromQuery } from '@hooks/useListProjectsBasicInfoFromQuery';
import useMe from '@hooks/useMe';
import useNewColors from '@hooks/useNewColors';
import {
  TASK_DESC_LENGTH_LIMIT,
  TASK_NAME_LENGTH_LIMIT,
} from '@src/constants/constants';
import Images from '@themes/images';
import theme from '@themes/theme';
import { localDateFromUTCDate } from '@utils/formatters/date';
import { submitFormIgnoringRejection } from '@utils/submitFormHandlers';

interface TaskFormProps {
  screenName?: string;
  // TA-10949 #24: lets the panel's header (rendered outside this Formik
  // tree, see task-panel.tsx) ask this form "is it safe to close?" instead
  // of closing unconditionally — see the effect below that keeps it current.
  attemptCloseRef?: React.MutableRefObject<(() => void) | null>;
}

// this form just for create task cause now no edit task on web, if you want to use this form for edit task, please check all the functions.
export const TaskForm: React.FC<TaskFormProps> = ({
  screenName,
  attemptCloseRef,
}) => {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const { me } = useMe();
  const { getMappedColor } = useNewColors();
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [selectedUserList, setSelectedUserList] = useState<UserSearchType[]>(
    []
  );
  const [
    showTaskNameDuplicateWaringModal,
    setShowTaskNameDuplicateWaringModal,
  ] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const {
    values,
    errors,
    touched,
    setTouched,
    submitForm,
    setFieldValue,
    isValid,
    isSubmitting,
    dirty,
  } = useFormikContext<FormValues>();
  const {
    id: taskId,
    projectId,
    projectUUId,
    priority,
    startDate,
    dueDate,
  } = values;

  const [getProjectLazy, { data }] = useGetProjectLazyQuery({
    variables: {
      id: projectUUId ?? '',
    },
  });
  useEffect(() => {
    if (projectId) {
      getProjectLazy({
        variables: {
          id: projectId,
        },
      });
    }
  }, [projectId]);

  const { getProject: selectedProject1 } = data || {
    selectedProject: undefined,
  };

  const {
    setIsTaskWebPanelOpen,
    setIdTaskDetailOpen,
    setIdProjectOfTaskDetailOpen,
    setUUIdProjectOfTaskDetailOpen,
    setIsTaskWebPanelMaximize,
  } = useWebDrawer();

  const { projects, loading: projectsLoading } =
    useListProjectsBasicInfoFromQuery({
      first: 100,
      sortBy: ProjectSortOption.NameAsc,
    });

  const [showSelectProject, setShowSelectProject] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | undefined>(
    undefined
  );

  useEffect(() => {
    if (selectedProject1) {
      setSelectedProject(selectedProject1);
    }
  }, [selectedProject1]);

  useEffect(() => {
    if (selectedProject) {
      setFieldValue('projectId', selectedProject?.id, true);
      setFieldValue('projectUUId', selectedProject?.uuid, false);
    }
  }, [selectedProject]);

  const [listTasksNamesUnderProject, { loading: loadingTasksNameData }] =
    useListTasksNamesUnderProjectLazyQuery();

  const priorities = [
    ['Low', 'LOW'],
    ['Medium', 'MEDIUM'],
    ['High', 'HIGH'],
  ];

  const buttonWidth = (Dimensions.get('screen').width - 32 - 12) / 3;
  const isLast = (index: number): boolean => !!((index + 1) % 3);

  useEffect(() => {
    if (!projects && !projectsLoading) {
      Alert.alert(
        t('models:tasks.noProjects.title'),
        t('models:tasks.noProjects.message'),
        [
          { text: t('shared:cancel'), onPress: () => navigation.goBack() },

          {
            text: t('shared:yes'),
            onPress: () =>
              navigation.navigate('projects-stack', {
                screen: 'project-create',
              }),
          },
        ]
      );
    }
  }, [projects]);

  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const onPressCancel = useCallback(() => {
    setIsCancelModalVisible(false);
    setIsTaskWebPanelOpen(false);
    setIsTaskWebPanelMaximize(false);
    setIdTaskDetailOpen('');
    setIdProjectOfTaskDetailOpen('');
    // TA-10949 #24 (review round 3): clear in lockstep with the id so a
    // later "open from a project" entry point never inherits this project's
    // stale uuid — see the setters' own comments at their call sites.
    setUUIdProjectOfTaskDetailOpen('');
  }, [
    setIsTaskWebPanelOpen,
    setIsTaskWebPanelMaximize,
    setIdTaskDetailOpen,
    setIdProjectOfTaskDetailOpen,
    setUUIdProjectOfTaskDetailOpen,
  ]);

  // TA-10949 #24: the panel header's X (task-panel.tsx) lives outside this
  // Formik tree and used to close unconditionally. It now calls this
  // instead — discard-confirm only when the form actually has changes,
  // otherwise close immediately, same rule as the Cancel button below.
  useEffect(() => {
    if (!attemptCloseRef) return;
    attemptCloseRef.current = () => {
      if (dirty) {
        setIsCancelModalVisible(true);
      } else {
        onPressCancel();
      }
    };
    return () => {
      attemptCloseRef.current = null;
    };
  }, [attemptCloseRef, dirty, onPressCancel]);

  const doCreateTask = async () => {
    try {
      const queryResult = await listTasksNamesUnderProject({
        variables: {
          projectId: selectedProject!.id,
          first:
            selectedProject!.tasksCount + selectedProject!.completedTasksCount,
        },
        fetchPolicy: 'cache-and-network',
      });
      const data = queryResult.data;
      if (
        data?.listTasks?.edges?.some((task) => {
          if (taskId) {
            return (
              task?.node?.name === values.name && task?.node?.id !== taskId
            );
          } else return task?.node?.name === values.name;
        })
      ) {
        setShowTaskNameDuplicateWaringModal(true);
      } else {
        submitFormIgnoringRejection(submitForm);
      }
    } catch {
      setErrorMsg(t('shared:exceptionMsg'));
    }
  };
  const createButton = () => {
    return (
      <Button
        disabled={
          !isValid ||
          Object.entries(touched).length < 1 ||
          loadingTasksNameData ||
          !values.name ||
          values.name.trim() === ''
        }
        isLoading={isSubmitting}
        variant='primary'
        onPress={doCreateTask}
        accessibilityLabel={
          taskId ? t('models:tasks.save.title') : t('models:tasks.create.title')
        }>
        {taskId ? t('models:tasks.save.title') : t('models:tasks.create.title')}
      </Button>
    );
  };

  const addMembers = () => {
    setTouched({ ...touched, projectId: true });
    setShowAddMembersModal(true);
  };

  useEffect(() => {
    setFieldValue(
      'users',
      selectedUserList.filter((t) => t.__typename !== 'StringSearchType')
    );
    setFieldValue(
      'inviteEmails',
      selectedUserList
        .filter((t) => t.__typename === 'StringSearchType')
        .map((t) => {
          return {
            email: t.title,
            role: t.role,
          };
        })
    );
  }, [selectedUserList]);

  const [visibleWidth, setVisibleWidth] = useState(300);

  const getDisplayName = (t: User) => {
    const { name, firstName, lastName } = t;
    return name ?? `${firstName ?? ''} ${lastName ?? ''}`.trim();
  };

  const renderUser = (item: UserSearchType) => {
    return (
      <Box
        flex={1}
        px='xs'
        py='m'
        mb='m'
        borderColor='grey02'
        borderWidth={1}
        borderRadius='xs'
        flexDirection='row'
        key={item.id}
        backgroundColor='white'
        alignItems='center'>
        {/* Left: render user / string item */}
        {item.__typename === 'User' ? (
          <Box flex={1} flexDirection='row' alignItems='center'>
            <Avatar
              disableAvatarPress={true}
              size='medium'
              id={item.id}
              avatar={item.avatar}
              label={getDisplayName(item as User)}
              isMultiple={false}
            />
            <Box ml='xs'>
              <Text
                variant='webBodySecondary'
                color='textPrimary'
                flexShrink={1}
                ellipsizeMode='tail'
                numberOfLines={1}>
                {getDisplayName(item as User)}
              </Text>
              {item.email && (
                <Text
                  flexShrink={1}
                  variant='webBodyHintSubline'
                  color='grey04'
                  marginTop='xxs'
                  ellipsizeMode='tail'
                  numberOfLines={1}>
                  {item.email}
                </Text>
              )}
            </Box>
          </Box>
        ) : (
          <Box
            key={item.title}
            flex={1}
            flexDirection='row'
            alignItems='center'>
            <EmailAvatar width={40} />
            <Box ml='xs'>
              <Text
                variant='webBodySecondary'
                color='textPrimary'
                flexShrink={1}
                ellipsizeMode='tail'
                numberOfLines={1}>
                {item.title}
              </Text>
            </Box>
          </Box>
        )}

        <TooltipWebV3
          minWidth='auto'
          marginOffset={0}
          title={
            <Text
              numberOfLines={0}
              maxWidth={80}
              flexShrink={1}
              variant='labelSmall'
              color='white'>
              Remove
            </Text>
          }
          placement='bottom-start'>
          <div style={{ flex: 1 }}>
            <SimpleHoverable
              onPress={() => {
                setSelectedUserList((prev) =>
                  prev.filter((t) => t.id !== item.id)
                );
              }}>
              {(hover) => (
                <Box
                  width={32}
                  height={32}
                  borderRadius='xs'
                  backgroundColor={hover ? 'grey01' : 'white'}
                  alignItems='center'
                  justifyContent='center'>
                  <Icon
                    color={hover ? 'alertRed' : 'grey05'}
                    variant='s'
                    name='Trash2'
                    marginHorizontal='none'
                  />
                </Box>
              )}
            </SimpleHoverable>
          </div>
        </TooltipWebV3>

        {/* Right: role selector for User only */}
        {!!me?.id && item.id === me.id ? (
          <Box
            width={86}
            mx='m'
            alignItems='center'
            justifyContent='flex-end'
            flexDirection='row'>
            <Text variant='webSmall' color='textPrimary' marginRight='xs'>
              {t('models:projects.taskList.assignee')}
            </Text>
          </Box>
        ) : (
          <BaseButtonV3
            noBorder={true}
            noRadius={false}
            noHover={false}
            buttonRadius='xs'
            // mr='xs'
            modalRadius={8}
            showHoverWhenDropdown={true}
            popModalComponent={({ onClose }) => (
              <TaskRoleSelectorWeb
                onRoleSelect={(role) => {
                  setSelectedUserList((prev) =>
                    prev.map((u) => (u.id === item.id ? { ...u, role } : u))
                  );
                  onClose();
                }}
                selectedRole={item.role}
              />
            )}>
            <Box
              width={86}
              mx='m'
              alignItems='center'
              justifyContent='flex-end'
              flexDirection='row'>
              <Text variant='webSmall' color='textPrimary' marginRight='xs'>
                {item.role === TaskMemberRole.Assignee
                  ? t('models:projects.taskList.assignee')
                  : t('models:projects.taskList.viewer')}
              </Text>
              <Icon color='grey05' variant='s' name='ChevronDown' />
            </Box>
          </BaseButtonV3>
        )}
      </Box>
    );
  };
  return (
    <Box flex={1} maxWidth={560} backgroundColor='white'>
      <ScrollView
        onLayout={(e) => {
          setVisibleWidth(e.nativeEvent.layout.width);
        }}
        showsVerticalScrollIndicator={false}
        flex={1}
        style={{ marginBottom: 80, marginTop: theme.spacing.l }}>
        <Field
          component={TextField}
          label='Name'
          accessibilityLabel='Name'
          name='name'
          isRequired
          placeholder={t('models:tasks.placeholders.name')}
          showCharsRemaining
          textInputProps={{ maxLength: TASK_NAME_LENGTH_LIMIT }}
          needToShowTailWhenSingleLine={true}
        />
        <Field
          component={TextField}
          label='Description'
          accessibilityLabel='Description'
          name='description'
          placeholder={t('models:tasks.placeholders.description')}
          showCharsRemaining
          textInputProps={{ maxLength: TASK_DESC_LENGTH_LIMIT }}
          needToShowTailWhenSingleLine={true}
        />

        <Box
          marginTop='s'
          marginBottom='xs'
          flexDirection='row'
          justifyContent='space-between'
          alignItems='center'>
          <Text variant='labelSmall'>
            {t('models:tasks.create.project.title')}
          </Text>
          <Text variant='metadataSecondary' color='textSecondary'>
            {t('shared:required')}
          </Text>
        </Box>
        {selectedProject && projectId ? (
          <Button
            variant='edit'
            fullWidth
            prefix={
              <Box
                alignItems='center'
                justifyContent='center'
                marginRight='xs'
                style={{
                  borderRadius: 5,
                  padding: 6,
                  backgroundColor: getMappedColor(selectedProject.color),
                }}>
                <Icon
                  name={
                    selectedProject.icon
                      ? (selectedProject.icon as keyof typeof Images)
                      : 'IconStart22'
                  }
                  variant='m'
                  color='white'
                />
              </Box>
            }
            onPress={() => setShowSelectProject(true)}
            isSmall
            accessibilityLabel='Add to project'>
            <Box style={{ maxWidth: visibleWidth - 28 - 48 }}>
              <Text
                numberOfLines={1}
                ellipsizeMode='tail'
                variant='labelSmall'
                color='textPrimary'
                flexShrink={1}>
                {selectedProject.name}
              </Text>
            </Box>
          </Button>
        ) : (
          <>
            <Button
              variant='edit'
              fullWidth
              onPress={() => setShowSelectProject(true)}
              isSmall
              accessibilityLabel='Add to project'>
              {t('models:tasks.create.project.add')}
            </Button>
            {errors.projectId && touched.projectId && (
              <Box flexDirection='row' alignItems='center' marginTop='s'>
                <Icon
                  name='AlertTriangle'
                  color='alertRed'
                  variant='s'
                  marginRight='xxs'
                />
                <Text variant='error'>{errors.projectId}</Text>
              </Box>
            )}
          </>
        )}

        <Text variant='labelSmall' marginTop='l'>
          {t('models:tasks.create.dates.title')}
        </Text>
        <Box marginTop='xs' flexDirection='row' justifyContent='space-between'>
          <Box flex={1} marginRight='s'>
            <Button
              variant={startDate ? 'editSelected' : 'edit'}
              onPress={() => {
                navigation.navigate('date-select', {
                  field: 'startDate',
                });
              }}
              prefixMarginRight='xs'
              prefix={
                <Icon
                  name='Calendar'
                  color={startDate ? 'textPrimary' : 'grey04'}
                />
              }
              isSmall
              accessibilityLabel='Start Date'>
              {startDate
                ? t('format:monthAndDay', {
                    val: localDateFromUTCDate(startDate),
                  })
                : t('models:tasks.create.dates.startDate')}
            </Button>
          </Box>
          <Box flex={1} marginLeft='s'>
            <Button
              variant={dueDate ? 'editSelected' : 'edit'}
              onPress={() => {
                navigation.navigate('date-select', { field: 'dueDate' });
              }}
              prefixMarginRight='xs'
              prefix={
                <Icon
                  name='Calendar'
                  color={dueDate ? 'textPrimary' : 'grey04'}
                />
              }
              isSmall
              accessibilityLabel='Due Date Button'>
              {dueDate
                ? t('format:monthAndDay', {
                    val: localDateFromUTCDate(dueDate),
                  })
                : t('models:tasks.create.dates.dueDate')}
            </Button>
          </Box>
        </Box>

        <Text marginTop='s' marginBottom='xs' variant='labelSmall'>
          {t('models:tasks.create.priority.title')}
        </Text>
        {/* PRIORITY */}
        <Box flexDirection='row' justifyContent='space-between'>
          {priorities.map(([key, value], index) => (
            <Box key={key} style={isLast(index) && { marginRight: 6 }} flex={1}>
              <Badge
                label={key}
                variant={
                  getPriority(value === priority ? priority : null).variant
                }
                // Unselected chips rest on grey01 — hover one step deeper
                // (TA-9005); the selected chip keeps its colored background.
                hoverBackgroundColor={value === priority ? undefined : 'grey02'}
                onPress={() => {
                  setTouched({ ...touched, priority: true });
                  setFieldValue('priority', value === priority ? null : value);
                }}
                width={buttonWidth}
                height={36}
                borderRadius='xxs'
              />
            </Box>
          ))}
        </Box>

        <Box flexDirection='column' marginTop='l'>
          <Box
            flexDirection='row'
            justifyContent='space-between'
            alignItems='center'>
            <Text variant='labelSmall' marginBottom='xs'>
              {t('models:projects.taskList.assignee')}
            </Text>
          </Box>

          {(selectedUserList?.length ?? 0) > 0 &&
            selectedUserList?.map((item) => renderUser(item))}

          <SimpleHoverable
            onPress={() => addMembers()}
            accessibilityLabel='Add Assignee'>
            {(isHovered) => (
              <Box
                px='l'
                py='m'
                flex={1}
                borderRadius='xs'
                flexDirection='row'
                alignItems='center'
                backgroundColor={
                  isHovered || showAddMembersModal ? 'grey02' : 'grey01'
                }>
                <Icon variant='s' name='Plus' color='greenSecondary' />
                <Text variant='webSmall' marginLeft='s' color='greenSecondary'>
                  {t('models:projects.taskList.assignee')}
                </Text>
              </Box>
            )}
          </SimpleHoverable>
        </Box>

        {/* Status: check code here on branch : TA-2464-please-remove-task-status-chips-i-e-not-started-ongoing-on-hold-completed-overdue OR ticket No TA-2464 */}
        {/* Task Phases, Requiring Task, and Task Dependency: check code here on branch:TA-3310-hide-functionality-task-phases-requiring-task-and-task-dependency OR ticket No TA-3310*/}
      </ScrollView>

      <Box
        flex={1}
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          left: 0,
        }}
        flexDirection='row'
        justifyContent='space-between'>
        <Box flex={1} marginRight='s'>
          <Button
            backgroundColor='white'
            borderColor='black'
            variant='cancel'
            color='black'
            onPress={() => {
              // TA-10949 #24: only confirm when there's something to lose.
              if (dirty) {
                setIsCancelModalVisible(true);
              } else {
                onPressCancel();
              }
            }}
            accessibilityLabel={t('shared:cancel')}>
            {t('shared:cancel')}
          </Button>
        </Box>
        <Box flex={1}>{createButton()}</Box>
      </Box>
      {showTaskNameDuplicateWaringModal && (
        <ConfirmModal
          showModal={showTaskNameDuplicateWaringModal}
          onClose={() => setShowTaskNameDuplicateWaringModal(false)}
          onPress={() => {
            setShowTaskNameDuplicateWaringModal(false);
            submitFormIgnoringRejection(submitForm);
          }}
          buttonText={t('models:tasks.proceed')}
          title={t('models:tasks.duplicateTask')}
          message={t('models:tasks.duplicateTaskMsg')}
        />
      )}
      {errorMsg !== '' && (
        <ConfirmModal
          isAlert={true}
          showModal={errorMsg != ''}
          onClose={() => setErrorMsg('')}
          onPress={() => setErrorMsg('')}
          title={t('shared:error')}
          buttonText='OK'
          message={errorMsg}
        />
      )}
      {isCancelModalVisible && (
        <CancelConfirmTaskAndProjectModal
          showModal={isCancelModalVisible}
          onClose={() => setIsCancelModalVisible(false)}
          onPress={onPressCancel}
          buttonTextOne={
            screenName == 'Create'
              ? t('models:tasks.cancel.btnCreateText')
              : t('models:tasks.cancel.btnEditText')
          }
          buttonText={
            screenName == 'Create'
              ? t('models:tasks.cancel.removeTask')
              : t('models:tasks.cancel.btnCancelText')
          }
          title={
            screenName == 'Create'
              ? t('models:tasks.cancel.title')
              : t('models:tasks.cancel.cancelEditingTitle')
          }
          message={
            screenName == 'Create'
              ? t('models:tasks.cancel.message')
              : t('models:tasks.cancel.editMessage')
          }
        />
      )}

      {showAddMembersModal && (
        <WebModalV3
          canOverflowModal={true}
          accessibilityLabel='Assign Task Member'
          onClose={() => {
            setShowAddMembersModal(false);
          }}
          visible={showAddMembersModal}
          width={800}
          height={412}>
          <TaskInviteOrAssignee
            selectedUser={selectedUserList}
            onAssignMember={(users) => {
              setSelectedUserList(users);

              setTouched({ ...touched, projectId: true });
            }}
            taskIdList={taskId ? [taskId] : []}
            onClose={() => {
              setShowAddMembersModal(false);
            }}
          />
        </WebModalV3>
      )}

      {showSelectProject && (
        <WebModal
          accessibilityLabel='Select Project'
          title='Select Project'
          onClose={() => {
            setShowSelectProject(false);
          }}
          visible={showSelectProject}
          width={504}>
          <ProjectSelectV3
            selectedProjectId={selectedProject?.id}
            onSelectFinish={(project) => {
              setSelectedProject(project);
            }}
            onClose={() => {
              setShowSelectProject(false);
            }}
          />
        </WebModal>
      )}
    </Box>
  );
};
